import { Spacing } from '@persona-id/design-tokens';
import { useState } from 'react';
import styled from 'styled-components';

import ChecksTable from 'dashboard/components/ChecksTable';
import Tooltip from 'dashboard/components/Tooltip';
import { TextWithDashedUnderline } from 'dashboard/components/Typography';
import { VerificationDetailProps } from 'dashboard/components/VerificationDetail/types';
import { BusinessWebsiteIdentityComparison, CheckName } from 'dashboard/models/check';
import { VerificationBusinessWebsite } from 'dashboard/models/verification';
import Attribute, { AttributeGrid } from 'design-system/components/Attribute';
import Spacer from 'design-system/components/Spacer';
import TabBar, { Tab } from 'design-system/components/TabBar';
import { Heading, Paragraph } from 'design-system/components/Text';
import { VerificationStatus } from 'lib/verification';

enum WebsiteInfoTab {
  Input = 'input',
  Extracted = 'extracted',
}

const StyledAttributeGrid = styled(AttributeGrid)`
  padding: 0 ${Spacing[4]} ${Spacing[2]};
`;

const ChecksInformationRow = styled('div')`
  display: grid;
  grid-template-columns: auto;
  grid-row-gap: ${Spacing[4]};
`;

interface DatabaseBusinessWebsiteVerificationProps extends VerificationDetailProps {
  verification: VerificationBusinessWebsite;
}

const ChecksHeaderContainer = styled('div')`
  display: grid;
  grid-template-columns: auto auto;
  align-items: center;
  padding: 0 ${Spacing[3]};
`;

const TitleContainer = styled('div')`
  justify-self: start;
`;

const ChecksHeader = ({ title }: { title: string }) => {
  return (
    <ChecksHeaderContainer>
      <TitleContainer>
        <Heading size='sm'>{title}</Heading>
      </TitleContainer>
    </ChecksHeaderContainer>
  );
};

const BusinessWebsiteVerification = ({
  verification,
}: DatabaseBusinessWebsiteVerificationProps) => {
  const {
    'website-url': websiteUrl,
    'redacted-at': redactedAt,
    'business-name': businessName,
    'address-street-1': addressStreet1,
    'address-street-2': addressStreet2,
    'address-city': addressCity,
    'address-subdivision': addressSubdivision,
    'address-postal-code': addressPostalCode,
    'address-country-code': addressCountryCode,
    checks,
    status,
  } = verification.attributes;

  const isRedacted = redactedAt != null;
  const notInitiated = status !== VerificationStatus.Initiated;

  const [selectedTab, setSelectedTab] = useState(WebsiteInfoTab.Input);

  const addressFull = [
    addressStreet1,
    addressStreet2,
    addressCity,
    addressSubdivision,
    addressPostalCode,
    addressCountryCode,
  ]
    .filter(Boolean)
    .join(' ');

  if (notInitiated && checks) {
    const identityComparisonCheck = checks.find(
      (check) => check.name === CheckName.BusinessWebsiteIdentityComparison,
    ) as BusinessWebsiteIdentityComparison;
    const identityRecordResults = identityComparisonCheck?.metadata?.['identity-record-results'];

    return (
      <>
        <TabBar>
          <Tab
            selected={selectedTab === WebsiteInfoTab.Input}
            onClick={() => setSelectedTab(WebsiteInfoTab.Input)}
          >
            Input
          </Tab>
          <Tab
            selected={selectedTab === WebsiteInfoTab.Extracted}
            onClick={() => setSelectedTab(WebsiteInfoTab.Extracted)}
          >
            Extracted
          </Tab>
        </TabBar>
        <Spacer height={2} />
        {selectedTab === WebsiteInfoTab.Input && (
          <ChecksInformationRow>
            <ChecksHeader title='Collected Fields' />
            <StyledAttributeGrid>
              <Attribute label='Website' value={websiteUrl} redacted={isRedacted} />
              <Attribute label='Business Name' value={businessName} redacted={isRedacted} />
              <Attribute label='Address' value={addressFull || '-'} redacted={isRedacted} />
            </StyledAttributeGrid>
          </ChecksInformationRow>
        )}
        {selectedTab === WebsiteInfoTab.Extracted && (
          <>
            <ChecksInformationRow>
              <ChecksHeader title='Business Names' />
              {identityRecordResults?.['business-names']?.map((businessName, index) => (
                <div style={{ paddingLeft: '16px' }} key={index}>
                  <Tooltip
                    key={index}
                    trigger='hover'
                    tooltip={
                      <Paragraph color='primary' size='md'>
                        {businessName['source-type']}
                      </Paragraph>
                    }
                    hideArrow={false}
                    placement='right'
                  >
                    <TextWithDashedUnderline key={index}>
                      {businessName['extracted-value']}
                    </TextWithDashedUnderline>
                  </Tooltip>
                </div>
              ))}
            </ChecksInformationRow>
            <Spacer height={4} />
            <ChecksInformationRow>
              <ChecksHeader title='Extracted Addresses' />
              {identityRecordResults?.['addresses']?.map((address, index) => (
                <div style={{ paddingLeft: '16px' }} key={index}>
                  <Tooltip
                    key={index}
                    trigger='hover'
                    tooltip={
                      <Paragraph color='primary' size='md'>
                        {address['source']}
                      </Paragraph>
                    }
                    hideArrow={false}
                    placement='right'
                  >
                    <TextWithDashedUnderline key={index}>
                      {address['extracted-value']}
                    </TextWithDashedUnderline>
                  </Tooltip>
                </div>
              ))}
            </ChecksInformationRow>
          </>
        )}
        <Spacer height={4} />
        <ChecksTable checks={checks} verification={verification} />
      </>
    );
  } else {
    return (
      <StyledAttributeGrid>
        <Attribute label='Website' value={websiteUrl} redacted={isRedacted} />
        <Attribute label='Business Name' value={businessName} redacted={isRedacted} />
        <Attribute label='Address' value={addressFull || '-'} redacted={isRedacted} />
      </StyledAttributeGrid>
    );
  }
};

export default BusinessWebsiteVerification;
